import { Info } from "@mui/icons-material";
import { Inline } from "../Inline/Inline";
import { Text } from "../Text/Text";
import { Stack } from "../Stack/Stack";
import { OrderDispatchOption } from "~/types";
import Skeleton from "react-loading-skeleton";
import { useDispatchTypeLabel } from "~/hooks/language";
import { HoursResult } from "~/hooks/hours";

export function StoreHours({
  skeleton,
  type,
  hours,
}: {
  skeleton: boolean;
  type: "STORE" | OrderDispatchOption | null;
  hours: HoursResult | null;
}) {
  const { label: typeLabel, loading: loadingLabel } =
    useDispatchTypeLabel(type);

  if (skeleton || loadingLabel || hours?.loading) {
    return (
      <Stack space="1">
        <Text font="special" weight="strong" size="large">
          <Skeleton width={80} />
        </Text>
        <Text font="special" weight="strong" size="large">
          <Skeleton width={160} />
        </Text>
      </Stack>
    );
  }

  if (!hours) return null;

  const { from, to, status, message } = hours;

  const openTime = timeFormatter.format(from);
  const closeTime = timeFormatter.format(to);

  return (
    <Stack space="1">
      {status === "now open" && (
        <NowOpen type={type} typeLabel={typeLabel} closeTime={closeTime} />
      )}

      {status === "closed" && (
        <Text
          font="special"
          weight="strong"
          size="large"
          color="jets-red"
          transform="uppercase"
        >
          {typeLabel} {status}
        </Text>
      )}

      {status === "closing soon" && (
        <Text
          font="special"
          weight="strong"
          size="large"
          color="warning"
          transform="uppercase"
        >
          {typeLabel} {status}
        </Text>
      )}

      <Text font="special" weight="strong" size="large">
        {status === "closed"
          ? `Opening ${openTime}`
          : `${openTime} to ${closeTime}`}
      </Text>

      {Boolean(message) && (
        <Inline space="1" alignY={"top"}>
          <Text color="warning">
            <Info />
          </Text>
          <Text
            color="warning"
            font="special"
            size="large"
            weight="semi-strong"
          >
            {message}
          </Text>
        </Inline>
      )}
    </Stack>
  );
}

function NowOpen({
  type,
  typeLabel,
  closeTime,
}: {
  type: "STORE" | OrderDispatchOption | null;
  typeLabel: string | null;
  closeTime: string;
}) {
  switch (type) {
    case "STORE":
      return (
        <Text font="special" weight="strong" size="large" transform="uppercase">
          {typeLabel} hours - now open
        </Text>
      );

    case "DELIVERY":
    case "PICK_UP":
    case "SERVICE":
      return (
        <Text font="special" weight="strong" size="large" transform="uppercase">
          {typeLabel} available until {closeTime}
        </Text>
      );

    default:
      return null;
  }
}

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: "numeric",
  minute: "numeric",
  hour12: true,
});

// TODO: Have removed the display of timezone for now

// const tz = Intl.DateTimeFormat(undefined, {
//   timeZoneName: "short",
// }).resolvedOptions().timeZone;
